<template>
  <div v-if="showInstallPrompt" class="pwa-install-prompt">
    <div class="pwa-install-content">
      <div class="pwa-install-header">
        <i class="fas fa-download"></i>
        <span>Install E-CAT App</span>
      </div>
      <p class="pwa-install-description">
        Install this application on your device for quick and easy access.
      </p>
      
      <!-- iOS specific instructions -->
      <div v-if="isIOS" class="browser-instructions">
        <p class="instruction-title">To install on iOS:</p>
        <ol>
          <li>Tap the <i class="fas fa-share-square"></i> Share button in Safari</li>
          <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
          <li>Tap <strong>"Add"</strong> in the top right</li>
        </ol>
      </div>

      <!-- Firefox instructions -->
      <div v-if="isFirefox" class="browser-instructions">
        <p class="instruction-title">To install in Firefox:</p>
        <ol>
          <li>Click the menu button <i class="fas fa-bars"></i> in the top right</li>
          <li>Select <strong>"More Tools"</strong></li>
          <li>Click <strong>"Create Shortcut to This Site..."</strong></li>
          <li>Check <strong>"Open in new window"</strong> for app-like experience</li>
          <li>Click <strong>"Create"</strong></li>
        </ol>
      </div>
      
      <div class="pwa-install-actions">
        <button v-if="!isIOS && !isFirefox" class="pwa-install-button" @click="installApp">
          Install
        </button>
        <button v-if="isIOS || isFirefox" class="pwa-install-button" @click="dismissPrompt">
          Got it
        </button>
        <button class="pwa-dismiss-button" @click="dismissPrompt">
          Not now
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { canInstall, installPWA } from '../../services/pwa.service';

export default {
  name: 'PWAInstallPrompt',
  
  setup() {
    const dismissed = ref(false);
    const isIOS = ref(false);
    const isFirefox = ref(false);
    
    // Show the prompt if installation is possible and user hasn't dismissed it
    const showInstallPrompt = computed(() => 
      (canInstall.value || isIOS.value || isFirefox.value) && !dismissed.value
    );

    // Install the PWA (for supported browsers)
    const installApp = () => {
      installPWA();
      dismissed.value = true;
    };

    // Dismiss the installation prompt
    const dismissPrompt = () => {
      dismissed.value = true;
      
      // Save to localStorage to prevent showing again for 7 days
      localStorage.setItem('pwaInstallDismissed', Date.now().toString());
    };

    // Check if we're on iOS
    const detectIOS = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(userAgent) && !window.MSStream;
    };

    // Check if we're using Firefox
    const detectFirefox = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return userAgent.indexOf('firefox') > -1;
    };

    // Check if we're in standalone mode (already installed)
    const isInStandaloneMode = () => 
      'standalone' in window.navigator && window.navigator.standalone;

    // Check if we should show the prompt based on localStorage and device
    onMounted(() => {
      // Detect browsers
      isIOS.value = detectIOS();
      isFirefox.value = detectFirefox();
      
      // Don't show if already in standalone mode (installed)
      if (isInStandaloneMode()) {
        dismissed.value = true;
        return;
      }
      
      // Check if recently dismissed
      const lastDismissed = localStorage.getItem('pwaInstallDismissed');
      if (lastDismissed) {
        const dismissedTime = parseInt(lastDismissed);
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        
        // If it's been less than 7 days since dismissal, don't show
        if (Date.now() - dismissedTime < sevenDaysInMs) {
          dismissed.value = true;
        }
      }
      
      // For iOS and Firefox, we'll show the prompt after a short delay
      if ((isIOS.value || isFirefox.value) && !dismissed.value) {
        // Only show instructions after 30 seconds of site usage
        setTimeout(() => {
          if (!localStorage.getItem('pwaInstallDismissed')) {
            dismissed.value = false;
          }
        }, 30000);
      }
    });

    return {
      showInstallPrompt,
      installApp,
      dismissPrompt,
      isIOS,
      isFirefox
    };
  }
}
</script>

<style scoped>
.pwa-install-prompt {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9998;
  background-color: white;
  color: #333;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  max-width: 350px;
  animation: slideIn 0.3s ease-out;
  border: 1px solid #e0e0e0;
}

@keyframes slideIn {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.pwa-install-content {
  padding: 16px;
}

.pwa-install-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-weight: bold;
  font-size: 16px;
}

.pwa-install-header i {
  margin-right: 8px;
  color: #013B82;
}

.pwa-install-description {
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
}

.browser-instructions {
  background-color: #f9f9f9;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
  text-align: left;
}

.instruction-title {
  font-weight: bold;
  margin-bottom: 8px;
  font-size: 14px;
}

.browser-instructions ol {
  padding-left: 20px;
  margin: 0;
}

.browser-instructions li {
  font-size: 13px;
  margin-bottom: 6px;
  line-height: 1.4;
}

.pwa-install-actions {
  display: flex;
  justify-content: space-between;
}

.pwa-install-button {
  background-color: #013B82;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.pwa-dismiss-button {
  background: transparent;
  color: #666;
  border: 1px solid #ccc;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 8px;
}

.pwa-install-button:hover {
  background-color: #002a5e;
}

.pwa-dismiss-button:hover {
  background-color: #f5f5f5;
}

@media (max-width: 768px) {
  .pwa-install-prompt {
    bottom: 0;
    left: 0;
    right: 0;
    max-width: 100%;
    border-radius: 0;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
}
</style>